import { apiBaseUrl } from '../../config/env';

export interface ReviewRatings {
  overall: number;
  payments: number;
  service: number;
  quality: number;
  working: number;
  communication: number;
  partiality: number;
  opportunities: number;
}

export interface BusinessReview {
  id: string;
  businessId: string;
  reviewerId: string;
  overallRating: number;
  paymentsRating: number;
  serviceRating: number;
  qualityRating: number;
  workingRating: number;
  communicationRating: number;
  partialityRating: number;
  opportunitiesRating: number;
  reviewText?: string;
  createdAt: string;
  updatedAt: string;
  reviewer: {
    id: string;
    name: string;
    photo?: string;
    profession?: string;
  };
}

export interface ReviewStats {
  totalReviews: number;
  averageRatings: ReviewRatings;
}

/**
 * Submit or update a review for a business
 */
export const submitReview = async (
  token: string,
  businessId: string,
  ratings: ReviewRatings,
  reviewText?: string
): Promise<{ success: boolean; review: BusinessReview }> => {
  const payload = {
    businessId,
    overallRating: ratings.overall,
    paymentsRating: ratings.payments,
    serviceRating: ratings.service,
    qualityRating: ratings.quality,
    workingRating: ratings.working,
    communicationRating: ratings.communication,
    partialityRating: ratings.partiality,
    opportunitiesRating: ratings.opportunities,
    reviewText,
  };
  
  console.log('API: Submitting to:', `${apiBaseUrl}/api/reviews`);
  console.log('API: Payload:', JSON.stringify(payload, null, 2));
  
  const response = await fetch(`${apiBaseUrl}/api/reviews`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  console.log('API: Response status:', response.status);
  console.log('API: Response ok:', response.ok);

  if (!response.ok) {
    const error = await response.json();
    console.error('API: Error response:', error);
    throw new Error(error.message || 'Failed to submit review');
  }

  const result = await response.json();
  console.log('API: Success response:', result);
  return result;
};

/**
 * Get all reviews for a business
 */
export const getBusinessReviews = async (
  token: string,
  businessId: string
): Promise<{ success: boolean; reviews: BusinessReview[] }> => {
  const response = await fetch(`${apiBaseUrl}/api/reviews/${businessId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch reviews');
  }

  return response.json();
};

/**
 * Get review statistics for a business
 */
export const getReviewStats = async (
  token: string,
  businessId: string
): Promise<{ success: boolean; stats: ReviewStats }> => {
  const response = await fetch(`${apiBaseUrl}/api/reviews/${businessId}/stats`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch review stats');
  }

  return response.json();
};

/**
 * Get current user's review for a business
 */
export const getMyReview = async (
  token: string,
  businessId: string
): Promise<{ success: boolean; review: BusinessReview | null }> => {
  const response = await fetch(`${apiBaseUrl}/api/reviews/${businessId}/my-review`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch your review');
  }

  return response.json();
};

/**
 * Delete your review for a business
 */
export const deleteReview = async (
  token: string,
  businessId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await fetch(`${apiBaseUrl}/api/reviews/${businessId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete review');
  }

  return response.json();
};
